import { useCallback, useRef, useState, type KeyboardEvent, type RefCallback } from "react";
import {
  firstEnabledCell,
  isGridNavKey,
  nextGridCell,
  type CellEnabled,
  type GridCell,
} from "@/lib/grid-navigation";

interface Options {
  rows: number;
  cols: number;
  /** Celler som ikke finnes eller er disabled hoppes over. */
  isEnabled?: CellEnabled;
}

export interface GridCellProps {
  ref: RefCallback<HTMLElement>;
  tabIndex: 0 | -1;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onFocus: () => void;
}

/**
 * VIB-2545: roving tabindex for en matrise av knapper/avkryssingsbokser.
 * Kun én celle er i Tab-rekkefølgen; piltaster, Home og End flytter fokus.
 * Space/Enter håndteres av cellen selv (checkbox toggler nativt).
 *
 * Brukes av SystemrollerCard og ProsjektrollerCard: spre `getCellProps(r, c)`
 * på det fokuserbare elementet i hver celle.
 */
export function useGridNavigation({ rows, cols, isEnabled = () => true }: Options) {
  const [current, setCurrent] = useState<GridCell | null>(null);
  const cells = useRef(new Map<string, HTMLElement>());
  const keyOf = (r: number, c: number) => `${r}:${c}`;

  const active =
    current && current.row < rows && current.col < cols && isEnabled(current.row, current.col)
      ? current
      : firstEnabledCell(rows, cols, isEnabled);

  const focusCell = useCallback((cell: GridCell) => {
    setCurrent(cell);
    cells.current.get(keyOf(cell.row, cell.col))?.focus();
  }, []);

  const getCellProps = (row: number, col: number): GridCellProps => ({
    ref: (el) => {
      if (el) cells.current.set(keyOf(row, col), el);
      else cells.current.delete(keyOf(row, col));
    },
    tabIndex: active && active.row === row && active.col === col ? 0 : -1,
    onKeyDown: (e) => {
      if (!isGridNavKey(e.key)) return;
      const next = nextGridCell({ row, col }, e.key, rows, cols, isEnabled);
      e.preventDefault();
      if (next) focusCell(next);
    },
    onFocus: () => setCurrent({ row, col }),
  });

  return { getCellProps, active };
}
